import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  X, 
  ArrowRight, 
  ArrowLeft, 
  Shield, 
  TrendingUp, 
  Bell, 
  History,
  Sparkles,
  Trophy,
  Check
} from "lucide-react";
import { cn } from "@/lib/utils";
import confetti from "canvas-confetti";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  highlight?: string;
  action?: {
    label: string;
    onClick?: () => void;
  };
}

const onboardingSteps: OnboardingStep[] = [
  {
    id: "welcome",
    title: "Bem-vindo ao Sentinela! 🎉",
    description: "Vamos fazer um tour rápido para você conhecer as principais funcionalidades do sistema.",
    icon: <Sparkles className="h-8 w-8 text-primary" />,
  },
  {
    id: "diagnostico",
    title: "Diagnóstico Financeiro",
    description: "Aqui você ativa o monitoramento do seu caixa. Basta inserir seus números mensais para receber uma análise completa com IA.",
    icon: <Shield className="h-8 w-8 text-primary" />,
    highlight: "[data-tour='diagnostico']",
    action: {
      label: "Quero fazer meu diagnóstico",
    }
  },
  {
    id: "indice",
    title: "Índice Sentinela (0-100)",
    description: "Este é o termômetro da saúde do seu negócio. Quanto mais alto, melhor. Acompanhe mensalmente para ver sua evolução.",
    icon: <TrendingUp className="h-8 w-8 text-success" />,
    highlight: "[data-tour='indice']",
  },
  {
    id: "historico",
    title: "Evolução Mensal",
    description: "Veja como seus números evoluem ao longo do tempo. O sistema aprende com cada diagnóstico e melhora suas previsões.",
    icon: <History className="h-8 w-8 text-blue-500" />,
    highlight: "[data-tour='historico']",
  },
  {
    id: "alertas",
    title: "Alertas Inteligentes",
    description: "Receba avisos proativos quando algo precisar de atenção. O Sentinela trabalha 24/7 para proteger seu caixa.",
    icon: <Bell className="h-8 w-8 text-amber-500" />,
    highlight: "[data-tour='alertas']",
  },
  {
    id: "complete",
    title: "Você está pronto! 🏆",
    description: "Parabéns! Agora você conhece o básico. Faça seu primeiro diagnóstico para desbloquear todo o poder do Sentinela.",
    icon: <Trophy className="h-8 w-8 text-amber-500" />,
  },
];

const ONBOARDING_KEY = "sentinela_interactive_onboarding_completed";

export function InteractiveOnboarding() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const step = onboardingSteps[currentStep];
  const progress = ((currentStep + 1) / onboardingSteps.length) * 100;

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed) {
      // Delay opening to let page load
      const timer = setTimeout(() => setIsOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleNext = useCallback(() => {
    setCompletedSteps(prev => [...prev, step.id]);
    
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete onboarding
      localStorage.setItem(ONBOARDING_KEY, "true");
      
      // Celebration confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#0066CC", "#00A3FF", "#FFD700"]
      });
      
      setIsOpen(false);
    }
  }, [currentStep, step.id]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setIsOpen(false);
  }, []);

  // Highlight target element
  useEffect(() => {
    if (!isOpen || !step.highlight) return;

    const target = document.querySelector(step.highlight);
    if (target) {
      target.classList.add("onboarding-highlight");
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    return () => {
      if (target) {
        target.classList.remove("onboarding-highlight");
      }
    };
  }, [isOpen, step.highlight]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60"
          aria-hidden="true"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          transition={{ type: "spring", damping: 25 }}
          className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:bottom-4 md:w-[420px] bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Progress bar */}
          <div className="px-6 pt-4">
            <Progress value={progress} className="h-1.5" />
          </div>

          {/* Close button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Pular tour"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Content */}
          <div className="p-6 pt-4">
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {/* Icon */}
              <div className="flex items-center gap-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.1 }}
                  className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center"
                >
                  {step.icon}
                </motion.div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Passo {currentStep + 1} de {onboardingSteps.length}
                  </p>
                </div>
              </div>

              {/* Description */}
              <p className="text-muted-foreground leading-relaxed">
                {step.description}
              </p>

              {/* Step indicators */}
              <div className="flex items-center gap-2 py-2">
                {onboardingSteps.map((s, index) => (
                  <motion.div
                    key={s.id}
                    initial={false}
                    animate={{
                      width: index === currentStep ? 24 : 8,
                      backgroundColor: index <= currentStep 
                        ? "hsl(var(--primary))" 
                        : "hsl(var(--muted))"
                    }}
                    className={cn(
                      "h-2 rounded-full transition-all",
                      completedSteps.includes(s.id) && "bg-success"
                    )}
                  />
                ))}
              </div>
            </motion.div>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Anterior
            </Button>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-muted-foreground"
              >
                Pular tour
              </Button>
              <Button onClick={handleNext} className="gap-2">
                {currentStep === onboardingSteps.length - 1 ? (
                  <>
                    <Check className="h-4 w-4" />
                    Concluir
                  </>
                ) : (
                  <>
                    Próximo
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

// Hook to reset onboarding
export function useInteractiveOnboarding() {
  const resetOnboarding = useCallback(() => {
    localStorage.removeItem(ONBOARDING_KEY);
    window.location.reload();
  }, []);

  const hasCompleted = localStorage.getItem(ONBOARDING_KEY) === "true";

  return { resetOnboarding, hasCompleted };
}
